import { createTheme, darken, alpha } from '@mui/material/styles'
import colors, { roles, STATE_LAYER_OPACITY, SHAPE_SCALE } from 'common/colors'

// Roboto is Material Design's own reference typeface — built for screen
// legibility at UI sizes, already self-hosted (images.farmtrx.app/fonts),
// and matches MUI's own default stack. The brand guide's print typefaces
// (Source Sans Pro, Open Sans) aren't used here; they're for print.
const FONT_FAMILY = '"Roboto", "Helvetica", "Arial", sans-serif'

// M3's elevation model is primarily a primary-color surface tint that grows
// with elevation, not shadow depth alone (m3.material.io/styles/elevation).
// m3.material.io doesn't publish the opacity-per-level numbers as a static
// table; these are sourced from Flutter's own M3 implementation (Google's
// reference implementation, elevation_overlay.dart) as the most precise
// public source found, with elevation treated as equivalent to MUI's
// `elevation` prop (both trace back to the same dp-based M2 scale).
const SURFACE_TINT_ELEVATION_TABLE = [
  { elevation: 0, opacity: 0 },
  { elevation: 1, opacity: 0.05 },
  { elevation: 3, opacity: 0.08 },
  { elevation: 6, opacity: 0.11 },
  { elevation: 8, opacity: 0.12 },
  { elevation: 12, opacity: 0.14 },
]

function surfaceTintOpacityForElevation(elevation) {
  const table = SURFACE_TINT_ELEVATION_TABLE
  if (elevation <= table[0].elevation) return table[0].opacity
  const last = table[table.length - 1]
  if (elevation >= last.elevation) return last.opacity
  for (let i = 0; i < table.length - 1; i++) {
    const lower = table[i]
    const upper = table[i + 1]
    if (elevation >= lower.elevation && elevation <= upper.elevation) {
      const t =
        (elevation - lower.elevation) / (upper.elevation - lower.elevation)
      return lower.opacity + t * (upper.opacity - lower.opacity)
    }
  }
  return last.opacity
}

const theme = createTheme({
  palette: {
    // `light`/`dark` are deliberately left unset here — MUI auto-derives
    // them from `main` (its own lighten/darken formula) and several
    // components read them for conventional purposes MUI already expects:
    // hover/press shading (see form.jsx), outlined borders, etc. M3's
    // "container" tones are a different concept (a pale, standalone
    // background meant to be used as-is, not diluted further) and are
    // wired in separately below via MuiAlert, not smuggled into `light`.
    primary: {
      main: roles.primary.main,
      contrastText: roles.primary.on,
    },
    secondary: {
      main: roles.secondary.main,
      contrastText: roles.secondary.on,
    },
    // Previously left at MUI's stock defaults, so every error/warning/
    // success/info Alert, Chip, Badge and color="..." button in the app
    // rendered generic red/orange/green/blue with no relation to the brand.
    error: {
      main: roles.error.main,
      contrastText: roles.error.on,
    },
    warning: {
      main: roles.warning.main,
      contrastText: roles.warning.on,
    },
    success: {
      main: roles.success.main,
      contrastText: roles.success.on,
    },
    info: {
      main: colors.info,
      contrastText: colors.white,
    },
    neutral: { main: '#F9F7F7' },
    text: {
      primary: colors.textPrimary,
      fontSize: '0.8rem', //not working
    },
    // MUI's stock action opacities are M2's (hover 4%, focus/activated 12%,
    // selected 8%) and drive the default hover/focus/press feedback on any
    // component we haven't explicitly restyled — IconButton, MenuItem, Tab,
    // Checkbox, Radio, plain ListItemButton, etc. Aligning these centrally
    // to M3's state-layer values means all of those get consistent feedback
    // in one place, instead of each one needing its own override.
    action: {
      hover: alpha('#000', STATE_LAYER_OPACITY.hover),
      hoverOpacity: STATE_LAYER_OPACITY.hover,
      selected: alpha('#000', STATE_LAYER_OPACITY.selected),
      selectedOpacity: STATE_LAYER_OPACITY.selected,
      focus: alpha('#000', STATE_LAYER_OPACITY.focus),
      focusOpacity: STATE_LAYER_OPACITY.focus,
      activatedOpacity: STATE_LAYER_OPACITY.pressed,
    },
  },
  typography: {
    fontFamily: FONT_FAMILY,
    allVariants: { color: colors.textPrimary },
    colorTextSecondary: { color: colors.textPrimary },
    // M3's type scale (m3.material.io/styles/typography) has five roles —
    // Display, Headline, Title, Body, Label — each in three sizes, with
    // weight and letter-spacing varying by role instead of one uniform bold
    // treatment. MUI has no native Display/Headline/Title naming, so this
    // keeps the h1–h6/subtitle/body/caption/overline API every component
    // already uses and restyles each to its nearest M3 value instead of
    // MUI's M2-era defaults. Display Medium/Small are deliberately unused —
    // the old (pre-M3) theme never set an explicit h1 size at all, so it
    // rendered at the browser's UA default of ~32px; mapping h1 to Display
    // Medium (45px) read as a size regression against that baseline, so the
    // whole scale starts one M3 role down, at Headline Large, instead.
    // Regular weight runs through Headline Large/Medium/Small (h1–h3) and
    // Title Large (h4); Title Medium/Small (h5/h6, and subtitle1/2) carry
    // M3's medium (500) weight, as does Label Small (overline).
    h1: {
      // Headline Large
      color: colors.brand,
      fontWeight: 400,
      fontSize: '2rem',
      lineHeight: 1.25,
      letterSpacing: '0px',
    },
    h2: {
      // Headline Medium
      color: colors.brand,
      fontWeight: 400,
      fontSize: '1.75rem',
      lineHeight: 1.2857,
      letterSpacing: '0px',
    },
    h3: {
      // Headline Small
      color: colors.brand,
      fontWeight: 400,
      fontSize: '1.5rem',
      lineHeight: 1.3333,
      letterSpacing: '0px',
    },
    h4: {
      // Title Large
      color: colors.brand,
      fontWeight: 400,
      fontSize: '1.375rem',
      lineHeight: 1.2727,
      letterSpacing: '0px',
    },
    h5: {
      // Title Medium
      color: colors.brand,
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.5,
      letterSpacing: '0.15px',
    },
    h6: {
      // Title Small
      color: colors.brand,
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.4286,
      letterSpacing: '0.1px',
      marginBlock: '0 !important',
    },
    subtitle1: {
      // Title Medium
      color: colors.brandLight,
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.5,
      letterSpacing: '0.15px',
    },
    subtitle2: {
      // Title Small
      color: colors.brandLight,
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.4286,
      letterSpacing: '0.1px',
    },
    body1: {
      // Body Large
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.5,
      letterSpacing: '0.5px',
    },
    body2: {
      // Body Medium
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.4286,
      letterSpacing: '0.25px',
    },
    caption: {
      // Body Small
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 1.3333,
      letterSpacing: '0.4px',
    },
    overline: {
      // Label Small
      fontWeight: 500,
      fontSize: '0.6875rem',
      lineHeight: 1.4545,
      letterSpacing: '0.5px',
    },
  },
  spacing: 8,
  // The default MUI falls back to for any component we haven't explicitly
  // restyled (Paper, Card, etc.) — happens to already match what was in use
  // everywhere as an unnamed default; this just makes that a documented M3
  // scale step instead of an implicit MUI default that looked the same.
  shape: {
    borderRadius: Number.parseInt(SHAPE_SCALE.extraSmall, 10),
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        html {
          font-size: 16px;
          height:100%;
        }

        body {
          background: ${colors.white};
          background-size: cover;
          background-repeat: no-repeat;
          color: ${colors.textPrimary};
          font-family: ${FONT_FAMILY};
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: optimizeLegibility;
          overflow: hidden;
          min-width: 300px;
          min-height: 200px;
        }

        h1, h2, h3, h4, h5, h6 {
          font-family: ${FONT_FAMILY};
          font-weight: 400;
          color: ${colors.brand};
          margin-block: 0;
        }

        h1 {
          font-size: 2rem;
          line-height: 1.25;
          letter-spacing: 0;
        }

        h2 {
          font-size: 1.75rem;
          line-height: 1.2857;
          letter-spacing: 0;
        }

        h3 {
          font-size: 1.5rem;
          line-height: 1.3333;
          letter-spacing: 0;
        }

        h4 {
          font-size: 1.375rem;
          line-height: 1.2727;
          letter-spacing: 0;
        }

        h5 {
          font-weight: 500;
          font-size: 1rem;
          line-height: 1.5;
          letter-spacing: 0.15px;
        }

        h6 {
          font-weight: 500;
          font-size: 0.875rem;
          line-height: 1.4286;
          letter-spacing: 0.1px;
        }

        input, select, textarea, button {
          font-family: inherit;
          font-size: inherit;
        }

        ::-webkit-scrollbar {
          width: 12px;
        }

        ::-webkit-scrollbar-track {
          position: absolute;
          opacity: 0;
          z-index: 1;
          background: rgba(222, 222, 222, .75);
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
          -webkit-transition: opacity 0.5s 0.5s ease-out;
          transition: opacity 0.5s 0.5s ease-out;
        }

        ::-webkit-scrollbar-thumb {
          position: absolute;
          top: 0;
          left: 0;
          width: 8px;
          height: 8px;
          background: rgba(150, 150, 150, .5);
          border-radius: 4px;
        }
        /* Chrome, Safari, Edge, Opera */
        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        /* Firefox */
        input[type=number] {
          -moz-appearance: textfield;
        }

        * {
          font-family: ${FONT_FAMILY} !important;
        }
      `,
    },
    MuiPaper: {
      // Applies to every Paper-based surface app-wide without touching
      // component files: Dialog, Menu (and therefore every Select/
      // Autocomplete dropdown), Popover, Card. Elevation 0 (or no elevation
      // prop passed to a bare <Paper>) resolves to 0% opacity, i.e. no
      // visible change from before.
      //
      // Matching on `elevation` alone isn't enough: Paper's `elevation` prop
      // defaults to 1 regardless of `variant`, but MUI only actually renders
      // the elevation-specific styling when variant="elevation" (confirmed
      // in Paper's own source) — variant="outlined" surfaces (e.g. the
      // support page's <Card variant="outlined">) still carry that default
      // elevation=1 prop value even though M3 says an outlined/flat surface
      // shouldn't have any tint. Matching `variant: 'elevation'` too avoids
      // painting a tint onto outlined surfaces that should stay flat.
      variants: Array.from({ length: 25 }, (_, elevation) => ({
        props: { elevation, variant: 'elevation' },
        style: {
          backgroundImage: `linear-gradient(${alpha(colors.brand, surfaceTintOpacityForElevation(elevation))}, ${alpha(colors.brand, surfaceTintOpacityForElevation(elevation))})`,
        },
      })),
    },
    MuiButton: {
      styleOverrides: {
        root: {
          // M3 buttons: sentence-case labels (not the M2 all-caps
          // convention) and fully pill-shaped corners.
          textTransform: 'none',
          borderRadius: SHAPE_SCALE.full,
          '&:active': {
            color: colors.brandLight,
            textDecoration: 'none',
          },
        },
      },
      variants: [
        {
          // Primary action (Generate, Print Report, Next/Finish, Save, the
          // confirm side of dialogs): the brand's gold CTA, navy text.
          props: { variant: 'contained', color: 'primary' },
          style: {
            backgroundColor: colors.brandSecondary,
            color: colors.brand,
            '&:hover': {
              backgroundColor: darken(
                colors.brandSecondary,
                STATE_LAYER_OPACITY.hover,
              ),
            },
          },
        },
        {
          // Cancel/dismiss: quiet outline instead of a solid fill, so it
          // never competes with the gold primary action next to it.
          props: { variant: 'contained', color: 'secondary' },
          style: {
            backgroundColor: 'transparent',
            color: colors.brand,
            border: `1px solid ${colors.brand}`,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: alpha(colors.brand, STATE_LAYER_OPACITY.hover),
              boxShadow: 'none',
            },
          },
        },
      ],
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '&:checked': {
            color: colors.brandLight,
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        // Colors only here — these are size-agnostic and safe on every
        // Switch instance, including size="small" (e.g. the archived/active
        // toggle on the clients/farms/fields pages).
        track: {
          backgroundColor: colors.grey,
          opacity: 1,
        },
        switchBase: {
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: colors.brand,
            opacity: 1,
          },
        },
        thumb: {
          backgroundColor: colors.white,
        },
      },
      variants: [
        {
          // MUI's "Android 12" switch example (its docs, Switch
          // customization) — verified against the actual generated CSS: the
          // track has no `position` of its own, so its icon pseudo-elements
          // resolve against the switch root (58px), not the track's shrunk
          // content box, which is what makes `left: 12`/`right: 12` land
          // without overlapping.
          //
          // Scoped to size="medium" only — these are absolute pixel values
          // measured against the medium root's 58x38 dimensions. Applying
          // them unconditionally broke size="small" (e.g. the
          // archived/active toggle), whose 40x24 root doesn't have room for
          // icons placed 12px from each edge.
          props: { size: 'medium' },
          style: {
            padding: 8,
            '& .MuiSwitch-track': {
              borderRadius: 11, // half the 22px track height (38px root - 2*8px padding)
              '&::before, &::after': {
                content: '""',
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
                width: 16,
                height: 16,
              },
              '&::before': {
                backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(colors.white)}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
                left: 12,
              },
              '&::after': {
                backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(colors.white)}" d="M19,13H5V11H19V13Z" /></svg>')`,
                right: 12,
              },
            },
            '& .MuiSwitch-thumb': {
              boxShadow: 'none',
              width: 16,
              height: 16,
              margin: 2,
            },
          },
        },
        {
          // size="small"'s own thumb (16px, MUI default) is taller than its
          // visible track (10px = 24px root - 2*7px padding), so it
          // overflowed the track — fixed by reducing root's padding to 4,
          // enlarging the track to (40-2*4)x(24-2*4) = 32x16, an exact fit
          // for the unchanged 16px thumb.
          //
          // That left the thumb filling the track's height edge-to-edge with
          // no visible gap. Shrinking the thumb to 12px opens up a 2px gap
          // top/bottom — but switchBase (which the thumb sits inside) is
          // positioned flush against root's *outer* box and auto-sizes to
          // thumb + 2*switchBase's own padding, and that total must keep
          // equalling root's height (24) for the existing vertical centering
          // to stay correct (verified: this is the exact mechanism that made
          // MUI's own unmodified default center correctly in the first
          // place — 16 + 2*4 = 24). So thumb=12 requires switchBase's own
          // padding = (24-12)/2 = 6 to preserve that equality. With that,
          // switchBase's total box stays 24x24 (unchanged), so the
          // horizontal checked-position transform (translateX(16px), still
          // untouched) also stays valid — verified numerically before
          // applying.
          props: { size: 'small' },
          style: {
            padding: 4,
            '& .MuiSwitch-thumb': {
              width: 12,
              height: 12,
            },
            '& .MuiSwitch-switchBase': {
              padding: 6,
            },
          },
        },
      ],
    },
    MuiSvgIcon: {
      styleOverrides: { root: { color: colors.greyMedium, opacity: 0.8 } },
    },
    MuiNativeSelect: {
      styleOverrides: {
        icon: {
          color: colors.brand,
          fontSize: '45px',
        },
        iconOpen: { transform: 'rotate(180deg)' },
        selectMenu: { height: '50px' },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        // Real padding, not a layout-detached hit area: an invisible
        // absolutely-positioned pseudo-element sized past the button's own
        // box can overlap onto a neighboring button's visible icon in any
        // tightly packed row (e.g. the notification open/dismiss actions in
        // containers/header.jsx), stealing taps meant for that neighbor —
        // and a disabled button can't shield its icon from a neighbor's
        // hit area doing that, since it no longer intercepts anything
        // itself. Real padding instead grows the button's actual box, so
        // the browser's normal layout keeps packed siblings apart and
        // targets can never overlap; the tradeoff is that rows relying on
        // padding:0 to sit flush now reflow to accommodate the real target.
        // Padding-around-content sizing (below) makes the button's own
        // width and height only as square as whatever it wraps happens to
        // be — MUI's own base style relies on that same assumption and
        // just works for a plain SvgIcon (always forced to a square 1em
        // box), but anything else nested in an IconButton (a custom icon
        // that isn't square, a size variant whose own hardcoded padding/
        // fontSize pairing doesn't land back on a square, etc.) throws the
        // width/height off — and since the hover/focus state layer is a
        // plain `border-radius: 50%` on this same box, a non-square box
        // renders it as an oval instead of a circle. `aspectRatio: 1`
        // forces height to track width exactly no matter the content, so
        // the state layer is always circular regardless of what's inside.
        root: {
          padding: '12px', // 24px medium icon + 12px each side = 48px target
          aspectRatio: '1',
        },
        sizeSmall: {
          padding: '10px', // 20px small icon + 10px each side = 40px target
          aspectRatio: '1',
        },
        // MUI's own size="large" variant (padding:12, 28px icon) is baked
        // into IconButton's base styles rather than driven by `root` above,
        // so `root`'s aspectRatio doesn't reliably reach it — every
        // size="large" instance in the app (user-accounts, operators,
        // crop-legend toolbars) rendered an oval hover highlight without
        // this explicit override.
        sizeLarge: {
          padding: '12px',
          aspectRatio: '1',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          paddingTop: 0,
          paddingBottom: 0,
        },
        gutters: {
          paddingLeft: '5px',
          paddingRight: 0,
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          alignItems: 'center',
          marginLeft: 0,
          marginRight: 0,
          md: {
            width: '30ch',
          },
          lg: {
            width: '35ch',
          },
          xl: {
            width: '40ch',
          },
          // '@media (max-width: 1280px) and (orientation:landscape)': {
          //   width: '35ch',
          // },
          // '@media (min-width: 1300px) and (orientation:landscape)': {
          //   width: '40ch',
          // },
          // '@media (max-width: 800px) and (orientation:portrait)': {
          //   width: '30ch',
          // },
        },
        labelPlacementStart: {
          display: 'flex',
          justifyContent: 'space-between',
          marginLeft: 0,
          marginRight: 0,
        },
        label: {
          fontSize: '1rem',
          fontWeight: 'normal',
        },
      },
    },
    MuiFormLabel: { styleOverrides: { root: { textDecoration: 'none' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: SHAPE_SCALE.extraSmall },
        input: { padding: '8px' },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          width: '25ch',
          '& .MuiFilledInput-input': {
            padding: '10px',
          },
          '& .MuiNativeSelect-select': { padding: '8px' },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          position: 'relative',
          width: 'auto',
          height: '25px',
          background: colors.brand,
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: { inputMarginDense: { paddingTop: '10px' } },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: { background: colors.white },
        root: {
          '.MuiOutlinedInput-root': {
            padding: '8px',
          },
          '.MuiAutocomplete-option': {
            paddingLeft: '3px',
          },
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: { color: colors.brandSecondaryLight },
        root: {
          '& .MuiStepLabel-label.Mui-active': {
            color: colors.brandSecondaryLight,
          },
          '& .MuiStepLabel-label.Mui-completed': {
            color: colors.brandSecondaryLight,
          },
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: '#685D53',
          fontWeight: 'bold',
          '&.Mui-active': { color: `${colors.brandSecondary} !important` },
          '&.Mui-completed': {
            color: `${colors.brandSecondaryLight} !important`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          height: '30px',
          background: colors.greyBackground,
          color: colors.brand,
        },
        body: {
          height: '30px',
          fontWeight: 'bold',
          paddingRight: 0,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        // M3's primary-tab indicator is a taller, rounded-top bar; MUI's
        // default is a thin (2px) flat underline (an M2 signature, like the
        // uppercase button labels were). Only real Tabs usage in the app
        // today is the admin Partners/Resellers tabs, but this applies to
        // any future one too since it's set at the theme level.
        indicator: {
          height: 3,
          borderRadius: `${SHAPE_SCALE.extraSmall} ${SHAPE_SCALE.extraSmall} 0 0`,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        // M3's container roles, applied directly. MUI's own Alert "standard"
        // formula takes palette[color].light and lightens it *again* by 90%,
        // which is right for MUI's stock mid-tone `.light` but washes our
        // already-pale container tones out to nearly nothing — and MUI v9's
        // overridesResolver only resolves `styles[variant]` (e.g. `standard`),
        // not a combined `standardError`-style key, so per-color overrides
        // have to nest a `.MuiAlert-color<X>` class selector inside `standard`
        // to win over the component's own internal per-color variants (verified
        // against the actual generated CSS, not assumed).
        standard: {
          '&.MuiAlert-colorError': {
            backgroundColor: roles.error.container,
            color: roles.error.onContainer,
          },
          '&.MuiAlert-colorWarning': {
            backgroundColor: roles.warning.container,
            color: roles.warning.onContainer,
          },
          '&.MuiAlert-colorSuccess': {
            backgroundColor: roles.success.container,
            color: roles.success.onContainer,
          },
          '&.MuiAlert-colorInfo': {
            backgroundColor: colors.infoLighter,
            color: colors.info,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: SHAPE_SCALE.small,
          borderTop: `4px solid ${colors.brand}`,
          boxShadow: '0 8px 32px rgba(0, 48, 87, 0.24)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: colors.brand,
          fontWeight: 700,
          fontSize: '1.25rem',
          padding: '20px 24px 16px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '8px 24px 20px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          borderTop: `1px solid ${colors.greyBorder}`,
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        columnHeader: {
          backgroundColor: colors.greyBackground,
          color: colors.brand,
          textTransform: 'uppercase',
        },
      },
    },
  },
})

export default theme
